<script lang="ts" setup>
import ChatInputV2, {
  ChatInputInstance,
} from "@/components/mobile/chat/v2/ChatInputV2.vue";
import { useDiscordGift } from "@/composable/useDiscordGift";
import { InputTypeEnum, ModelEnum, ModelList } from "@/enums/chatEnums";
import { useChat } from "@/stores/chat";
import { useChatSession } from "@/stores/chatSession";
import { useGlobalModal } from "@/stores/globalModal";
import { useGlobalUser } from "@/stores/globalUser";
import { ChatMessage, Mate, MateSession } from "@/types/entity.d";
import NSFWModalCrystalNotEnough from "@/views/NSFW/Modal/NSFWModalCrystalNotEnough.vue";
import NSFWModalPowerNotEnough from "@/views/NSFW/Modal/NSFWModalPowerNotEnough.vue";
import { Popup, showLoadingToast } from "vant";
import { computed, ref, onMounted } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
// Props & Emit
const props = withDefaults(
  defineProps<{
    enableHistoryMode?: boolean;
    mate: Mate;
    currentSession: MateSession | null;
    isPrivate: boolean;
    isHistoryMode?: boolean;
    isPostingMsg: "start" | "end" | false;
    isStreamReplying: boolean;
    myMessage: string;
    streamMsg: string;
    isInputFocus?: boolean;
    shouldLogin?: boolean;
    hideMsgUI?: boolean;
    enableAvatar?: boolean;
  }>(),
  {
    isHistoryMode: false,
    enableHistoryMode: false,
    isInputFocus: false,
    shouldLogin: false,
    hideMsgUI: false,
  }
);

const emit = defineEmits<{
  (
    e: "onStreamFinish",
    data: {
      userMsg: ChatMessage;
      assistantMsg?: ChatMessage;
    }
  ): void;
  (e: "onInputFocus"): void;
  (e: "update:isHistoryMode", value: boolean): void;
  (e: "update:isPostingMsg", value: "start" | "end" | false): void;
  (e: "update:isStreamReplying", value: boolean): void;
  (e: "update:myMessage", value: string): void;
  (e: "update:streamMsg", value: string): void;
  (e: "update:isInputFocus", value: boolean): void;
  (e: "update:hideMsgUI", value: boolean): void;
  (e: "onPostMsgClick", toNames?: string[]): void;
  (e: "update:enableAvatar", value: boolean): void;
}>();
const { t } = useI18n();
// Constant
const ChatActions = ref([
  {
    icon: "mingcute--chat-3-line",
    label: "Chats.actions.say",
    value: InputTypeEnum.SAY,
  },
  // {
  //   value: InputTypeEnum.DO,
  //   label: "Chats.actions.do",
  //   icon: "mingcute--tool-line",
  // },
  {
    value: InputTypeEnum.SNEAK,
    label: "Chats.actions.sneak",
    icon: "mdi--eye-off",
  },
  {
    value: InputTypeEnum.MOVE,
    label: "Chats.actions.move",
    icon: "mingcute--walk-line",
  },
  {
    value: InputTypeEnum.FIGHT,
    label: "Chats.actions.fight",
    icon: "mingcute--sword-line",
  },
  // {
  //   icon: "mingcute--backpack-line",
  //   label: computed(() => t("Chats.actions.items")),
  //   value: InputTypeEnum.ITEM,
  // },
  // {
  //   icon: "mingcute--ai-line",
  //   label: computed(() => t("Chats.actions.mixed")),
  //   value: InputTypeEnum.MIXED,
  // },
]);
// State
const router = useRouter();
const userStore = useGlobalUser();
const session = useChatSession();
const chat = useChat();
const globalModal = useGlobalModal();

const inputMsg = ref<string>("");
const showMateCard = ref(false);
const showModelList = ref(false);
const currentMsgType = ref<InputTypeEnum>(InputTypeEnum.SAY);
const actionTargets = ref<string[]>([]);
const inputPlaceholder = computed(() => {
  if (currentMsgType.value === InputTypeEnum.MESSAGE) return undefined;
  return ChatActions.value.find((type) => type.value === currentMsgType.value)
    ?.label;
});

const temperature = ref(0.35);
const temperatureComputed = computed({
  get: () => temperature.value,
  set: (val) => {
    const num = typeof val === "number" ? val : parseFloat(val);
    if (num > 1) {
      temperature.value = 1;
    } else if (num < 0) {
      temperature.value = 0;
    } else {
      temperature.value = num;
    }
  },
});

const { giftLink, showDiscordGiftLink } = useDiscordGift();

// Method
const onChangeModel = async (model: ModelEnum) => {
  const loading = showLoadingToast("");
  try {
    const targetModel = ModelList.find((item) => item.value === model);
    await session.updateSession(props.currentSession!.id!, {
      model: model,
      isNsfw: targetModel?.isNsfwSupport ? 1 : 0,
    });
  } finally {
    loading.close();
  }
};

export interface ChatFooterInstance {
  postMessage: ChatInputInstance["postMessage"];
}

const chatInputRef = ref<ChatInputInstance | null>(null);

defineExpose({ postMessage: computed(() => chatInputRef.value?.postMessage) });

const isSayOrDo = computed(() =>
  [InputTypeEnum.SAY, InputTypeEnum.DO].includes(currentMsgType.value)
);

const onSelectTarget = (id: number) => {
  let text = "";
  const { characterzz, positionzz } = props.currentSession!.chatScene!;
  if (currentMsgType.value === InputTypeEnum.FIGHT) {
    text = `Try to attack ${characterzz?.find((v) => v.id === id)?.mate?.name}`;
  }
  if (currentMsgType.value === InputTypeEnum.MOVE) {
    text = `Try to move to ${positionzz?.find((v) => v.id === id)?.name}`;
  }
  if (currentMsgType.value === InputTypeEnum.SNEAK) {
    text = `Try to sneak to ${positionzz?.find((v) => v.id === id)?.name}`;
  }

  emit("update:enableAvatar", false);
  chatInputRef.value?.postMessage({
    inputType: currentMsgType.value,
    targetId: id,
    text,
  });
};

const clickChangeInputType = (type: InputTypeEnum) => {
  currentMsgType.value = type;
  if (type === InputTypeEnum.SAY) {
    emit("update:enableAvatar", true);
  } else {
    emit("update:enableAvatar", false);
  }
};

onMounted(() => {
  if (!props.currentSession?.chatScene?.characterzz?.length) {
    ChatActions.value = [
      {
        icon: "mingcute--chat-3-line",
        label: "Chats.actions.say",
        value: InputTypeEnum.SAY,
      },
      {
        value: InputTypeEnum.DO,
        label: "Chats.actions.do",
        icon: "mingcute--tool-line",
      },
    ];
  }

  globalModal.openServerUpgradingModal();
});
</script>
<template>
  <div class="chat-darken-linear-background-b">
    <div
      v-if="showDiscordGiftLink"
      class="flex gap-1 py-1 px-2 text-base-content/50-content items-center justify-between"
    >
      <div class="flex gap-1 items-center">
        <i class="iconify mingcute--discord-fill" />
        <a
          :href="giftLink"
          class="cursor-pointer underline text-xs sm:text-sm hover:text-base-content/50-content text-base-content/50-content"
          target="_blank"
          v-html="$t('Message.joinDiscordTip')"
        >
        </a>
      </div>
      <button
        class="btn-xs btn btn-ghost btn-square"
        @click="showDiscordGiftLink = false"
      >
        <i class="iconify mingcute--close-line" />
      </button>
    </div>

    <TargetStatusBar :session-id="currentSession!.id!" />

    <!-- Action Bar -->
    <div class="flex items-center px-2 pt-2 flex-wrap gap-2 mb-2">
      <div class="join rounded">
        <VDropdown :delay="0" :triggers="['click']" placement="top">
          <slot>
            <button
              class="btn btn-xs join-item btn-primary w-24 flex items-center gap-1 px-0 justify-center"
            >
              <i
                class="iconify"
                :class="
                  ChatActions.find((v) => v.value === currentMsgType)?.icon
                "
              />
              <span>
                {{
                  $t(
                    ChatActions.find((v) => v.value === currentMsgType)?.label!
                  )
                }}
              </span>
              <i class="iconify mdi--chevron-down" />
            </button>
          </slot>
          <template #popper>
            <div
              class="flex flex-col join join-vertical text-base-content rounded-none p-1"
            >
              <slot name="header" />
              <div
                v-for="action in ChatActions"
                class="min-w-24 flex gap-2 items-center px-2 py-1 hover:bg-base-200 rounded cursor-pointer overflow-hidden"
                :key="action.value"
                v-close-popper
                @click="clickChangeInputType(action.value)"
              >
                <span
                  class="w-4 aspect-square flex items-center justify-center shrink-0"
                >
                  <i
                    v-if="currentMsgType === action.value"
                    class="iconify mingcute--check-line text-xs"
                  />
                </span>
                <span class="flex-grow line-clamp-1">
                  {{ $t(action.label) }}
                </span>
              </div>
            </div>
          </template>
        </VDropdown>
      </div>
      <div v-if="currentSession" class="flex items-center gap-2">
        <div>To:</div>
        <ChatActionTargetSelector
          v-model:action-targets="actionTargets"
          :mate="mate"
          :mate-session="currentSession!"
          :current-msg-type="currentMsgType"
          @target-id="onSelectTarget"
        />
      </div>
      <VDropdown v-if="isSayOrDo" :delay="0" :triggers="['click']">
        <button class="btn btn-xs btn-square btn-primary rounded">
          <i class="iconify mingcute--thermometer-line" />
        </button>
        <template #popper>
          <div
            class="flex flex-col join join-vertical text-base-content rounded-none"
          >
            <span
              class="join-item p-2 gap-1 border-b border-base-content/10 flex flex-col"
            >
              <span class="flex items-center gap-1 text-xs">
                <i class="iconify mingcute--thermometer-line text-xs" />
                {{ $t("Chats.temperature.title") }} (0 - 1,
                {{ $t("Chats.chatScene.edit.default") }}: 0.35)
              </span>
              <span class="text-xs text-base-content/50">
                {{ $t("Chats.temperature.tip") }}
              </span>
            </span>
            <!-- 进度条 -->
            <span class="join-item flex items-center gap-1 p-2">
              <span class="flex items-center gap-1">
                <span class="text-xs">{{ $t("Chats.temperature.low") }}</span>
                <input
                  class="range range-xs"
                  type="range"
                  min="0"
                  max="1"
                  step="0.05"
                  v-model="temperatureComputed"
                />
                <span class="text-xs">{{ $t("Chats.temperature.high") }}</span>
              </span>
              <input
                class="input input-xs input-bordered rounded w-12"
                v-model="temperatureComputed"
                type="number"
                max="1"
                min="0"
              />
            </span>
          </div>
        </template>
      </VDropdown>
    </div>
    <ChatInputV2
      ref="chatInputRef"
      v-model:input-msg="inputMsg"
      v-show="isSayOrDo"
      :class="{
        hidden: shouldLogin,
        block: !shouldLogin,
      }"
      :input-type="currentMsgType"
      :is-posting-message="isPostingMsg"
      :is-stream-replying="isStreamReplying"
      :mate="mate"
      :mate-session-id="currentSession!.id!"
      :my-message="myMessage"
      :placeholder="$t(inputPlaceholder!)"
      :stream-msg="streamMsg"
      :temperature="temperature"
      :to-name="actionTargets"
      @blur="emit('update:isInputFocus', false)"
      @focus="
        emit('onInputFocus');
        emit('update:isInputFocus', true);
      "
      @update:is-posting-message="emit('update:isPostingMsg', $event)"
      @update:is-stream-replying="emit('update:isStreamReplying', $event)"
      @update:my-message="emit('update:myMessage', $event)"
      @update:stream-msg="emit('update:streamMsg', $event)"
      @on-stream-finish="emit('onStreamFinish', $event)"
      @on-post-msg-click="emit('onPostMsgClick', $event)"
    >
    </ChatInputV2>
    <div
      v-if="shouldLogin"
      class="p-2"
      @click="userStore.loginModal.show = true"
    >
      <button class="w-full btn glass btn-primary">
        {{ $t("Buttons.loginToChat") }}
      </button>
    </div>
    <!-- 角色信息  -->
    <Popup v-model:show="showMateCard" position="bottom" round>
      <div class="py-4 flex flex-col gap-4">
        <div class="font-bold px-6 text-lg">{{ mate.name }}</div>
        <van-cell-group inset>
          <van-cell
            :label="mate?.bio || $t('Message.notBio')"
            :style="{
              '--van-cell-text-color': 'hsl(var(--s))',
              '--van-cell-label-color': 'hsl(var(--bc))',
              '--van-cell-label-font-size': '0.875rem',
            }"
            :title="$t('game.characterIntro')"
            class="text-sm"
          />
        </van-cell-group>
        <van-cell-group inset>
          <van-cell
            :title="$t('Chats.mateHome')"
            is-link
            @click="
              router.push({
                name: 'mateHome',
                params: { uuid: mate?.uuid },
              })
            "
          >
            <template #icon>
              <div class="pr-4"><i class="iconfont icon-profile" /></div>
            </template>
          </van-cell>
        </van-cell-group>
      </div>
    </Popup>
    <NSFWModalCrystalNotEnough
      v-model:show="chat.showInsufficientCrystalPopup"
      :miss-amount="chat.missCrystalAmount"
    />
    <NSFWModalPowerNotEnough
      v-model:show="chat.showInsufficientPowerPopup"
      :miss-amount="chat.missPowerAmount"
    />
    <ModelSelectionPopup
      v-model:show="showModelList"
      :initial-model="currentSession?.model"
      @confirm="onChangeModel"
    />
    <PersonaSelectPopup
      v-model:show="session.showPersonaSelectPopup"
      :session="currentSession"
    />
  </div>
</template>
<style scoped>
.chat-ghost-btn {
  @apply btn-xs lg:btn-sm btn backdrop-blur-md bg-black/30 text-white border-0 hover:bg-black/80;
}

.chat-ghost-btn.active {
  @apply bg-primary/80 hover:bg-primary;
}

.heart-icon-color:before {
  background-image: var(--colorful-gradient-r);
  background-clip: text;
  -webkit-text-fill-color: transparent;
}

.chat-darken-linear-background-b {
  background: linear-gradient(rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.7) 100%);
}
</style>
